'use client';

import { useEffect, useMemo, useState } from 'react';
import { useFinance } from '@/hooks/useFinance';
import { EChart, type EChartsOption } from '@/components/charts/EChart';

interface Props {
  goalId: string;
  width?: number;
  height?: number;
}

export function BalanceMicroSparkline({ goalId, width = 56, height = 18 }: Props) {
  const { fetchGoalContributionHistory } = useFinance();
  const [points, setPoints] = useState<number[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGoalContributionHistory(goalId)
      .then((entries) => {
        if (cancelled) return;
        let acc = 0;
        const cum = entries.map((e) => (acc += e.amount));
        setPoints(cum.length ? cum : null);
      })
      .catch(() => setPoints(null));
    return () => {
      cancelled = true;
    };
  }, [goalId, fetchGoalContributionHistory]);

  const option = useMemo<EChartsOption>(() => {
    if (!points || points.length < 2) return {};
    const trending = points[points.length - 1] > points[0];
    const color = trending ? '#059669' : '#94a3b8';
    return {
      tooltip: { show: false },
      grid: { left: 0, right: 0, top: 1, bottom: 1 },
      xAxis: { type: 'category', show: false, boundaryGap: false, data: points.map((_, i) => i) },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'line',
          data: points,
          smooth: true,
          silent: true,
          showSymbol: false,
          lineStyle: { color, width: 1.25, opacity: 0.85 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: `${color}33` },
                { offset: 1, color: 'rgba(255,255,255,0)' },
              ],
            },
          },
          endLabel: { show: false },
        },
      ],
    };
  }, [points]);

  if (!points || points.length < 2 || points.every((p) => p === 0)) return null;

  return <EChart option={option} style={{ width, height }} />;
}
